// orderHist page strings (views/OrderHistV2View.vue). Keys are read as $t('orderHist.<key>').
// Field codes (SA/TR/TO/KH, FPS/FPK, ycdb/ycdb2), pack aliases and server data stay as-is.
export default {
  en: {
    capture: {
      pack: 'Copy this package as a picture',
      detail: 'Copy this package table as a picture',
      receipt: 'Copy this receipt as a picture',
      week: 'Copy this whole week as a picture',
      addresses: 'Copy these addresses as a picture',
      note: 'Copy this note as a picture',
      notes: 'Copy the notes shown as a picture (the first 20 at most)',
      body: 'Copy the whole page as a picture',
      failed: 'Could not copy the picture. Use Chrome or Edge and try again.',
    },
    search: {
      placeholder: 'Customer phone…',
      clear: 'Clear',
      newCustomer: 'New customer',
    },
    bar: {
      week: 'Week {week}',
      settings: 'View settings',
      thisWeek: 'This week',
      thisWeekTitle: 'Jump to the current week',
      latest: 'Latest order',
      latestTitle: "Jump to the customer's most recent package",
      fullNotes: 'Full kitchen notes',
      fullNotesTitle: 'Kitchen notes written out under each meal on the package in detail view',
      last3: 'Last 3 months only',
      last3Hint: 'Faster load; untick for the full history.',
      last3Title: 'Only load the last 3 months of order history so a customer opens faster. Untick to load the whole history.',
      autoClean: 'Auto-clean address',
      autoCleanHint: 'Fill new ward and district from the typed address.',
      autoCleanTitle: 'Detect the district and new ward from the address when creating a customer or adding an address.',
      notesFollow: 'Notes follow the week',
      notesFollowHint: 'Filter notes by the selected week.',
      notesFollowTitle: 'Automatically filter the note list by week.',
      togglePerD: 'Show / hide the PerD tab',
      tabOrder: 'Tab order',
      deprecated: 'This tab will be removed in a future version',
      deprecatedInfo: 'This tab will be removed. Its details and bill are now in the card under the profile (Info / Bill).',
      deprecatedDetails: 'This tab will be removed. Shippers, addresses and kitchen notes are now on the week board: click the selected package again.',
      tabOrderHint: 'The first tab of each bar opens when the page loads. Saved in this browser.',
      tabOrderMain: 'Main panel',
      tabOrderSide: 'Card under the profile',
      tabOrderReset: 'Reset to default',
      moveUp: 'Move up',
      moveDown: 'Move down',
    },
    legend: {
      holiday: 'holiday',
      meals: 'meals',
    },
    profile: {
      copy: 'Copy name / address / phone',
      notFound: 'Not found',
      noCustomer: 'No customer',
      notFoundHint: 'No customer has this phone.',
      searchHint: 'Search a phone above.',
      showDietTraits: 'Show Diet & Traits',
      hideDietTraits: 'Hide Diet & Traits',
      address: 'Address',
      addresses: 'Addresses',
      otherAddressOne: '{count} other address',
      otherAddressMany: '{count} other addresses',
      diet: 'Diet',
      traits: 'Traits',
      dietTraitsSummary: 'Diet ({diet}) · Traits ({traits}) — show',
    },
    notes: {
      title: 'Notes',
      types: {
        fitfood: 'Fitfood',
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS',
        FFKhac: 'FPK',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      // hover text of the note type buttons
      typeTitles: {
        fitfood: 'Fitfood',
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS · FP Ship',
        FFKhac: 'FPK · FP Other',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      channels: {
        phone: 'Phone',
        facebook: 'Facebook',
        other: 'Other',
      },
      placeholder: 'Note description  (Ctrl+Enter to create)',
      pickWeek: 'Pick a week for the note',
      tooOld: 'Week {week} — notes only within 5 weeks',
      targetPack: 'Week {week} · {pack} #{id}',
      week: 'Week {week}',
      create: 'Create',
      createHint: 'Write the note and pick its week to create it',
      tooOldHint: 'Week {week} is more than 5 weeks back: notes can only go on the last 5 weeks. Pick a newer week on the board.',
      search: 'Search notes',
      empty: 'No notes.',
      status: {
        done: 'done',
        pending: 'pending',
      },
      // Note LLM (_docs/plan_note_llm.md)
      llm: {
        title: 'Standard',
        working: 'Standardising…',
        unavailable: 'Not standardised now: the note is saved as typed and standardised later.',
        // why DeepSeek gave no standard version (the preview's error_kind)
        reason: {
          busy: 'DeepSeek is busy: the note is saved as typed and standardised later.',
          balance: 'DeepSeek is out of balance: the note is saved as typed and standardised after a top-up.',
          network: 'Can\'t reach DeepSeek: the note is saved as typed and standardised later.',
          auth: 'DeepSeek refused the API key: the note is saved as typed and standardised once the key is fixed.',
          answer: 'DeepSeek\'s answer was unusable: the note is saved as typed and standardised later.',
        },
        asTyped: 'Save as typed',
        asTypedTitle: 'Save only what you typed, without the standard version',
        auto: 'Standard (auto)',
        pending: 'Not processed by LLM',
        failed: 'LLM failed',
        unsure: 'LLM not sure',
      },
    },
    board: {
      weeksGap: 'wk',
      loadedRecent: 'Last 3 months only',
      loadedAll: 'From the start of last year',
      week: 'week',
      holiday: 'holiday',
      thisWeek: '(This Week)',
      meals: '{count} meals',
      addrRow: 'ADD',
      shipRow: 'SHIP',
      days: {
        mon: 'mon',
        tue: 'tue',
        wed: 'wed',
        thu: 'thu',
        fri: 'fri',
        sat: 'sat',
        sun: 'sun',
      },
    },
    tabs: {
      orderList: 'Orders',
      info: 'Info',
      details: 'Details',
      customerBalance: 'Balance',
      days: 'Export meals',
    },
    orders: {
      title: "Customer's orders",
      clear: 'Clear',
      search: 'Search orders',
      cols: {
        week: 'Week',
        id: 'ID',
        pack: 'Pack',
        shipLoc: 'Ship loc',
        total: 'Total',
        mealNote: 'Meal note',
        meals: 'Meals',
      },
      empty: 'No orders.',
      ticked: '{count} ticked',
      tickAll: 'Tick / untick all listed packages',
    },
    info: {
      packInfo: 'Package info',
      pack: 'Pack',
      mealRequests: 'Meal requests',
      mealNote: 'Meal note',
      shipRequests: 'Delivery requests',
      shipLoc: 'Ship loc',
      address: 'Address',
      weeklyNote: 'Weekly note (2)',
      fixedNote: 'Fixed note (1)',
      receipt: 'Package details',
      customer: 'Customer:',
      weekParen: '(week {week})',
      subtotal: 'Subtotal',
      discount: 'Discount',
      total: 'Total due',
      disclaimer: 'Not equivalent to an invoice or proof of payment',
      empty: 'Select a package on the board or in the order list.',
    },
    side: {
      info: 'Info',
      bill: 'Bill',
      gom: 'Multi',
      od: 'PerD',
    },
    range: {
      from: 'From',
      to: 'To',
      snapped: 'Whole weeks: {from} → {to}',
      empty: 'No package in these weeks.',
      noCustomer: 'Enter a customer\'s phone number first: the days come from their packages.',
    },
    od: {
      pick: '— Pick a period —',
      partial: 'This is the earliest order loaded: weeks before it were not loaded, so its data may be incomplete.',
      empty: 'Pick a period: its packages show as one bill. Periods come from the "OD ... EOD" part of the meal note; packages without one form a weekly period.',
      week: 'Week {week}',
      packs: '{count} pkg',
      title: 'Period details',
      mealLine: '{pack} · {m} meals/day × {days} days',
      shipLine: 'Ship {loc} × {days} days',
      sideLine: '{name} × {qty}',
      diff: 'Difference (other)',
      label: 'Order {text}',
      compare: 'Compare',
      compareTitle: 'Compare bills',
      gomFormat: 'Gom bill format (per week)',
      periodFormat: 'Period format',
      match: 'Totals match',
      mismatch: 'Totals differ by {diff}',
      sumOfPackages: 'Sum of the packages',
      daily: 'By day',
      dailyTitle: 'Day by day',
      exportExcel: 'Export Excel',
      off: 'Off {days} days from {from} to {to}',
      offA: 'Off',
      offB: 'days from {from} to {to}',
      offDay: 'Off',
      unassignedShip: 'Ship not matched to a day',
      unassignedMeal: 'Package price without a delivery day',
      daysTotal: 'Days total',
      check: 'Check: sum of the rows = total',
      cols: {
        week: 'Week',
        date: 'Date',
        dow: 'Day',
        pack: 'Package',
        packId: 'Pkg ID',
        packCode: 'Package code',
        kh: 'Extra (KH)',
        shipDis: 'Ship area',
        meal: 'Meals',
        ship: 'Ship',
        dayTotal: 'Day total',
      },
    },
    gomBill: {
      title: 'Details of purchases',
      empty: 'Tick packages in the Orders list to combine them into one bill.',
      loading: 'Loading…',
      error: 'Could not load the bill for these packages.',
      wrong: 'THIS BILL DOES NOT ADD UP',
      packAdj: 'Pack price adjustment',
    },
    receipt: {
      thanksAnn: 'Thank you for choosing Ann Kitchen!',
    },
    invoice: {
      pack: '{pack} ({meals} meals)',
      ship: 'Ship {loc} ({days} days)',
      shipOtherPack: ' (for ID: {id})',
      shipAdj: 'Shipping price adjustment',
      side: '{name} ({qty} x)',
      sideAdj: 'Side product price adjustment',
      special: 'ycdb service fee',
      other: 'Other',
    },
    details: {
      title: 'Package details for week',
      idParen: '(id: {id})',
      rows: {
        shipDis: 'ship loc',
        deliverTimeslot: 'time',
        shipper: 'shipper',
        address: 'address',
      },
      empty: 'No packages with meals in this week.',
    },
    balance: {
      title: 'Customer balance over the last 12 months',
      search: 'Search transactions',
      cols: {
        no: 'No.',
        date: 'Date',
        week: 'Week',
        food: 'Food',
        paid: 'Paid',
        due: 'Owed (credit)',
        description: 'Description',
      },
      empty: 'No transactions.',
    },
    addresses: {
      title: 'Addresses',
      active: 'Active',
      all: 'All',
      newAddress: 'New address',
      main: 'Main address',
      inactive: 'inactive',
      newWard: 'new ward',
      district: 'district',
      ward: 'ward',
      makeMain: 'Make main',
      deactivate: 'Deactivate',
      activate: 'Activate',
      empty: 'No other addresses.',
    },
    form: {
      newCustomer: 'New customer',
      newAddress: 'New address',
      phone: 'Customer phone',
      phonePlaceholder: '10 digits',
      email: 'Email',
      emailPlaceholder: 'email',
      name: 'Customer name',
      namePlaceholder: 'Customer name',
      address: 'Address',
      addressPlaceholder: 'Customer address',
      newWard: 'New ward',
      newWardPlaceholder: 'Type to search new ward…',
      district: 'District',
      districtPlaceholder: 'Type to search district…',
      ward: 'Ward',
      wardHint: '(from auto-clean)',
      cancel: 'Cancel',
      createCustomer: 'Create customer',
      createAddress: 'Create address',
      close: 'Close',
    },
    errors: {
      addressTooShort: 'The customer Address must have at least 10 characters',
      addressExists: 'This address already exists for this customer',
      pickCustomer: 'Please select a customer first',
      pickNewWard: 'Please select a New Ward',
      pickDistrict: 'Please select a District',
      nameMissing: 'The customer Name is missing',
      phoneMissing: 'The customer Phone is missing',
      addressMissing: 'The customer Address is missing',
      emailMissing: 'The customer Email is missing',
      phoneDigits: 'The customer Phone must have 10 digits',
      emailInvalid: 'The customer Email is not valid',
    },
  },
  vi: {
    capture: {
      pack: 'Chụp ảnh gói này',
      detail: 'Chụp ảnh bảng gói này',
      receipt: 'Chụp ảnh phiếu này',
      week: 'Chụp ảnh cả tuần này',
      addresses: 'Chụp ảnh danh sách địa chỉ',
      note: 'Chụp ảnh ghi chú này',
      notes: 'Chụp ảnh các ghi chú đang hiện (tối đa 20 ghi chú đầu)',
      body: 'Chụp ảnh toàn trang',
      failed: 'Không sao chép được ảnh. Hãy dùng Chrome hoặc Edge và thử lại.',
    },
    search: {
      placeholder: 'Số điện thoại khách…',
      clear: 'Xoá',
      newCustomer: 'Khách hàng mới',
    },
    bar: {
      week: 'Tuần {week}',
      settings: 'Tuỳ chọn hiển thị',
      thisWeek: 'Tuần này',
      thisWeekTitle: 'Chọn tuần hiện tại',
      latest: 'Đơn mới nhất',
      latestTitle: 'Đến gói ăn gần nhất của khách',
      fullNotes: 'Ghi chú bếp đầy đủ',
      fullNotesTitle: 'Hiện ghi chú bếp dưới từng phần ăn trên gói đang xem chi tiết',
      last3: 'Chỉ 3 tháng gần nhất',
      last3Hint: 'Mở nhanh hơn; bỏ chọn để xem toàn bộ lịch sử.',
      last3Title: 'Chỉ tải lịch sử đơn hàng trong 3 tháng gần nhất để mở khách hàng nhanh hơn. Bỏ chọn để tải toàn bộ lịch sử.',
      autoClean: 'Tự làm sạch địa chỉ',
      autoCleanHint: 'Tự điền phường mới và quận từ địa chỉ đã nhập.',
      autoCleanTitle: 'Tự động nhận diện Quận và Phường mới từ địa chỉ khi tạo khách hàng hoặc thêm địa chỉ.',
      notesFollow: 'Ghi chú theo tuần',
      notesFollowHint: 'Lọc ghi chú theo tuần đang chọn.',
      notesFollowTitle: 'Tự động lọc danh sách Note theo tuần.',
      togglePerD: 'Bật / tắt tab PerD',
      tabOrder: 'Thứ tự tab',
      deprecated: 'Tab này sẽ bị xoá trong phiên bản sau',
      deprecatedInfo: 'Tab này sẽ bị xoá. Thông tin gói và bill đã có ở thẻ dưới hồ sơ (Info / Bill).',
      deprecatedDetails: 'Tab này sẽ bị xoá. Shipper, địa chỉ và ghi chú bếp đã có trên bảng tuần: bấm thêm lần nữa vào gói đang chọn.',
      tabOrderHint: 'Tab đầu tiên của mỗi thanh được mở sẵn khi tải trang. Lưu trên trình duyệt này.',
      tabOrderMain: 'Bảng chính',
      tabOrderSide: 'Thẻ dưới hồ sơ',
      tabOrderReset: 'Về mặc định',
      moveUp: 'Lên',
      moveDown: 'Xuống',
    },
    legend: {
      holiday: 'ngày nghỉ',
      meals: 'phần ăn',
    },
    profile: {
      copy: 'Sao chép tên / địa chỉ / SĐT',
      notFound: 'Không tồn tại',
      noCustomer: 'Chưa chọn khách',
      notFoundHint: 'Không có khách hàng với SĐT này.',
      searchHint: 'Tìm SĐT ở ô phía trên.',
      showDietTraits: 'Hiện Diet & Traits',
      hideDietTraits: 'Ẩn Diet & Traits',
      address: 'Địa chỉ',
      addresses: 'Địa chỉ',
      otherAddressOne: '{count} địa chỉ khác',
      otherAddressMany: '{count} địa chỉ khác',
      diet: 'Diet',
      traits: 'Traits',
      dietTraitsSummary: 'Diet ({diet}) · Traits ({traits}) — hiện',
    },
    notes: {
      title: 'Ghi chú',
      // note types stay in English in both languages
      types: {
        fitfood: 'Fitfood',
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS',
        FFKhac: 'FPK',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      // hover text of the note type buttons
      typeTitles: {
        fitfood: 'Fitfood',
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS · FP Ship',
        FFKhac: 'FPK · FP Khác',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      channels: {
        phone: 'Điện thoại',
        facebook: 'Facebook',
        other: 'Khác',
      },
      placeholder: 'Nội dung ghi chú  (Ctrl+Enter để tạo)',
      pickWeek: 'Chọn tuần cho ghi chú',
      tooOld: 'Tuần {week} — chỉ ghi chú trong 5 tuần',
      targetPack: 'Tuần {week} · {pack} #{id}',
      week: 'Tuần {week}',
      create: 'Tạo',
      createHint: 'Viết nội dung và chọn tuần để tạo ghi chú',
      tooOldHint: 'Tuần {week} đã quá 5 tuần: chỉ thêm ghi chú cho 5 tuần gần nhất. Hãy chọn tuần mới hơn trên bảng.',
      search: 'Tìm ghi chú',
      empty: 'Chưa có ghi chú.',
      status: {
        done: 'xong',
        pending: 'đang chờ',
      },
      // Note LLM (_docs/plan_note_llm.md)
      llm: {
        title: 'Bản chuẩn',
        working: 'Đang chuẩn hoá…',
        unavailable: 'Chưa chuẩn hoá được: ghi chú lưu như đã gõ và được chuẩn hoá sau.',
        reason: {
          busy: 'DeepSeek đang quá tải: ghi chú lưu như đã gõ và được chuẩn hoá sau.',
          balance: 'DeepSeek hết tiền: ghi chú lưu như đã gõ và được chuẩn hoá khi nạp thêm tiền.',
          network: 'Không kết nối được DeepSeek: ghi chú lưu như đã gõ và được chuẩn hoá sau.',
          auth: 'DeepSeek từ chối khoá API: ghi chú lưu như đã gõ và được chuẩn hoá khi khoá được sửa.',
          answer: 'DeepSeek trả lời không dùng được: ghi chú lưu như đã gõ và được chuẩn hoá sau.',
        },
        asTyped: 'Lưu như đã gõ',
        asTypedTitle: 'Chỉ lưu phần đã gõ, không lưu bản chuẩn',
        auto: 'Bản chuẩn (tự động)',
        pending: 'Chưa xử lý LLM',
        failed: 'LLM lỗi',
        unsure: 'LLM chưa chắc',
      },
    },
    board: {
      weeksGap: 'tuần',
      loadedRecent: 'Chỉ 3 tháng gần nhất',
      loadedAll: 'Từ đầu năm trước',
      week: 'tuần',
      holiday: 'ngày nghỉ',
      thisWeek: '(Tuần này)',
      meals: '{count} meals', // kept in English on the week board
      addrRow: 'ĐC',
      shipRow: 'SHIP',
      days: {
        mon: 'T2',
        tue: 'T3',
        wed: 'T4',
        thu: 'T5',
        fri: 'T6',
        sat: 'T7',
        sun: 'CN',
      },
    },
    tabs: { // kept in English in both languages
      orderList: 'Orders',
      info: 'Info',
      details: 'Details',
      customerBalance: 'Balance',
      days: 'Export meals',
    },
    orders: {
      title: 'Danh sách order của khách',
      clear: 'Bỏ chọn',
      search: 'Tìm đơn hàng',
      cols: {
        week: 'Tuần',
        id: 'ID',
        pack: 'Gói',
        shipLoc: 'Khu ship',
        total: 'Tổng',
        mealNote: 'Ghi chú phần ăn',
        meals: 'Phần',
      },
      empty: 'Chưa có đơn hàng.',
      ticked: 'Đã tick {count} gói',
      tickAll: 'Tick / bỏ tick tất cả gói đang hiện',
    },
    info: {
      packInfo: 'Thông tin gói ăn',
      pack: 'Gói',
      mealRequests: 'Yêu cầu món ăn',
      mealNote: 'Ghi chú phần ăn',
      shipRequests: 'Yêu cầu ship',
      shipLoc: 'Khu ship',
      address: 'Địa chỉ',
      weeklyNote: 'Note 2 hàng tuần',
      fixedNote: 'Note 1 cố định',
      receipt: 'Chi tiết gói ăn',
      customer: 'Khách hàng:',
      weekParen: '(tuần {week})',
      subtotal: 'Tổng giá',
      discount: 'Giảm giá',
      total: 'Tổng cộng',
      disclaimer: 'Không có giá trị thay hoá đơn hay chứng từ thanh toán',
      empty: 'Chọn một gói ăn trên bảng tuần hoặc trong danh sách đơn.',
    },
    side: {
      info: 'Info',
      bill: 'Bill',
      gom: 'Multi',
      od: 'PerD',
    },
    range: {
      from: 'Từ',
      to: 'Đến',
      snapped: 'Trọn tuần: {from} → {to}',
      empty: 'Không có gói nào trong các tuần này.',
      noCustomer: 'Nhập số điện thoại khách hàng trước: bảng lấy theo các gói của khách.',
    },
    od: {
      pick: '— Chọn đợt —',
      partial: 'Đây là đợt sớm nhất đã tải: các tuần trước đó chưa được tải nên dữ liệu đợt này có thể chưa đủ.',
      empty: 'Chọn một đợt để xem bill gộp các gói của nó. Đợt lấy từ phần "OD ... EOD" trong ghi chú phần ăn; gói không có thì tính theo tuần.',
      week: 'Tuần {week}',
      packs: '{count} gói',
      title: 'Chi tiết gói ăn',
      mealLine: '{pack} · {m} phần/ngày × {days} ngày',
      shipLine: 'Ship {loc} × {days} ngày',
      sideLine: '{name} × {qty}',
      diff: 'Chênh lệch khác',
      label: 'Order {text}',
      compare: 'So sánh',
      compareTitle: 'So sánh bill',
      gomFormat: 'Dạng Gom bill (theo tuần)',
      periodFormat: 'Dạng theo đợt',
      match: 'Tổng khớp',
      mismatch: 'Tổng lệch {diff}',
      sumOfPackages: 'Tổng các gói',
      daily: 'Theo ngày',
      dailyTitle: 'Chi tiết theo ngày',
      exportExcel: 'Xuất Excel',
      off: 'Nghỉ {days} ngày từ {from} tới {to}',
      offA: 'Nghỉ',
      offB: 'ngày từ {from} tới {to}',
      offDay: 'Nghỉ',
      unassignedShip: 'Ship không gắn được vào ngày',
      unassignedMeal: 'Tiền gói không có ngày giao',
      daysTotal: 'Cộng các ngày',
      check: 'Kiểm tra: tổng các dòng = tổng cộng',
      cols: {
        week: 'Tuần',
        date: 'Ngày',
        dow: 'Thứ',
        pack: 'Gói',
        packId: 'ID gói',
        packCode: 'Mã gói',
        kh: 'KH',
        shipDis: 'Khu ship',
        meal: 'Tiền ăn',
        ship: 'Ship',
        dayTotal: 'Cộng ngày',
      },
    },
    gomBill: {
      title: 'Chi tiết gói ăn',
      empty: 'Tick các gói trong danh sách Orders để gom thành một bill.',
      loading: 'Đang tải…',
      error: 'Không tải được bill cho các gói này.',
      wrong: 'BILL NÀY BỊ SAI',
      packAdj: 'Điều chỉnh giá gói',
    },
    receipt: {
      thanksAnn: 'Cảm ơn bạn đã chọn Ann Kitchen!',
    },
    invoice: {
      pack: '{pack} ({meals} phần)',
      ship: 'Ship {loc} ({days} ngày)',
      shipOtherPack: ' (theo ID: {id})',
      shipAdj: 'Điều chỉnh giá ship',
      side: '{name} ({qty} x)',
      sideAdj: 'Điều chỉnh giá sản phẩm phụ',
      special: 'Phí dịch vụ ycdb',
      other: 'Khác',
    },
    details: {
      title: 'Thông tin chi tiết các package trong tuần',
      idParen: '(id: {id})',
      rows: {
        shipDis: 'khu ship',
        deliverTimeslot: 'giờ giao',
        shipper: 'shipper',
        address: 'địa chỉ',
      },
      empty: 'Tuần này không có gói ăn nào.',
    },
    balance: {
      title: 'Số dư khách hàng trong 1 năm qua',
      search: 'Tìm giao dịch',
      cols: {
        no: 'STT',
        date: 'Ngày',
        week: 'Tuần',
        food: 'Tiền ăn',
        paid: 'Tiền trả',
        due: 'Thiếu (dư)',
        description: 'Diễn giải',
      },
      empty: 'Chưa có giao dịch.',
    },
    addresses: {
      title: 'Địa chỉ',
      active: 'Đang dùng',
      all: 'Tất cả',
      newAddress: 'Thêm địa chỉ',
      main: 'Địa chỉ chính',
      inactive: 'ngưng dùng',
      newWard: 'phường mới',
      district: 'quận',
      ward: 'phường',
      makeMain: 'Đặt làm chính',
      deactivate: 'Ngưng dùng',
      activate: 'Dùng lại',
      empty: 'Không có địa chỉ khác.',
    },
    form: {
      newCustomer: 'Khách hàng mới',
      newAddress: 'Thêm địa chỉ',
      phone: 'SĐT khách hàng',
      phonePlaceholder: '10 chữ số',
      email: 'Email',
      emailPlaceholder: 'email',
      name: 'Tên khách hàng',
      namePlaceholder: 'Tên khách hàng',
      address: 'Địa chỉ',
      addressPlaceholder: 'Địa chỉ khách hàng',
      newWard: 'Phường mới',
      newWardPlaceholder: 'Gõ để tìm phường mới…',
      district: 'Quận',
      districtPlaceholder: 'Gõ để tìm quận…',
      ward: 'Phường',
      wardHint: '(tự nhận diện)',
      cancel: 'Huỷ',
      createCustomer: 'Tạo khách hàng',
      createAddress: 'Tạo địa chỉ',
      close: 'Đóng',
    },
    errors: {
      addressTooShort: 'Địa chỉ khách hàng phải có ít nhất 10 ký tự',
      addressExists: 'Địa chỉ này đã có cho khách hàng',
      pickCustomer: 'Vui lòng chọn khách hàng trước',
      pickNewWard: 'Vui lòng chọn Phường mới',
      pickDistrict: 'Vui lòng chọn Quận',
      nameMissing: 'Thiếu tên khách hàng',
      phoneMissing: 'Thiếu SĐT khách hàng',
      addressMissing: 'Thiếu địa chỉ khách hàng',
      emailMissing: 'Thiếu email khách hàng',
      phoneDigits: 'SĐT khách hàng phải có 10 chữ số',
      emailInvalid: 'Email khách hàng không hợp lệ',
    },
  },
}
